// components/BitmexOrderBook.js
import React, { useState, useEffect, useRef } from 'react';

// --- CONFIG ---
const WS_URL = process.env.REACT_APP_BITMEX_WS || '/api/bitmex-ws/ws/bitmex';

// --- HELPERS ---
const formatPrice = (num) => num?.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0.0';
const formatSize = (num) => num?.toLocaleString('en-US') || '0';

// --- SUB-COMPONENT: Single Row with Flash Animation ---
const OrderRow = ({ item, maxVolume, type, theme }) => {
  const prevSize = useRef(item.size);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    if (item.size > prevSize.current) setFlash('flash-up');
    else if (item.size < prevSize.current) setFlash('flash-down');
    else setFlash('');
    
    prevSize.current = item.size;
    const timer = setTimeout(() => setFlash(''), 200);
    return () => clearTimeout(timer);
  }, [item.size]);

  const depthWidth = Math.min((item.size / maxVolume) * 100, 100);
  const color = type === 'bid' ? '#00bfa5' : '#ff5253';
  const bgBase = type === 'bid' ? 'rgba(0, 191, 165, 0.15)' : 'rgba(255, 82, 83, 0.15)';

  return (
    <div 
      className={flash}
      style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        padding: '3px 12px',
        fontSize: '0.85rem',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        fontFamily: 'Roboto Mono, monospace',
        fontWeight: '500',
        transition: 'background-color 0.15s ease-out'
      }}
    >
      {/* Depth Bar */}
      <div style={{
        position: 'absolute', top: 0, [type === 'bid' ? 'right' : 'left']: 0,
        width: `${depthWidth}%`, height: '100%', backgroundColor: bgBase, zIndex: 0,
        transition: 'width 0.2s ease-out'
      }} />

      {/* Text Data */}
      <span style={{ zIndex: 1, color: theme.textSecondary }}>{formatSize(item.size)}</span>
      <span style={{ zIndex: 1, color: color, fontWeight: '600' }}>{formatPrice(item.price)}</span>
      
      <style>{`
        .flash-up { background-color: rgba(0, 191, 165, 0.3) !important; }
        .flash-down { background-color: rgba(255, 82, 83, 0.2) !important; opacity: 0.7; }
      `}</style>
    </div>
  );
};

// --- HOOK: WebSocket Logic ---
const useBitmexOrderBook = (symbol) => {
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const bookMap = useRef(new Map());
  const wsRef = useRef(null);

  useEffect(() => {
    const bitmexSymbol = symbol === 'BTC' ? 'XBTUSD' : `${symbol}USD`;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setError(null);
      ws.send(JSON.stringify({ op: 'subscribe', args: [`orderBookL2_25:${bitmexSymbol}`] }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data.data || data.table !== 'orderBookL2_25') return;

        const { action, data: updates } = data;

        if (action === 'partial') {
          bookMap.current.clear();
          updates.forEach(item => bookMap.current.set(item.id, item));
        } else if (action === 'insert' || action === 'update') {
          updates.forEach(item => {
            const existing = bookMap.current.get(item.id) || {};
            bookMap.current.set(item.id, { ...existing, ...item });
          });
        } else if (action === 'delete') {
          updates.forEach(item => bookMap.current.delete(item.id));
        }

        const items = Array.from(bookMap.current.values());
        setOrderBook({
          bids: items.filter(i => i.side === 'Buy').sort((a, b) => b.price - a.price).slice(0, 25),
          asks: items.filter(i => i.side === 'Sell').sort((a, b) => a.price - b.price).slice(0, 25)
        });
      } catch (err) { console.error(err); }
    };

    ws.onerror = () => {
      setError(WS_URL.includes('8960') ? 'Proxy Error' : 'Connection Error');
      setConnected(false);
    };

    ws.onclose = () => {
      setConnected(false);
      setTimeout(() => { if (wsRef.current?.readyState === 3) setConnected(false); }, 3000);
    };

    return () => ws.close();
  }, [symbol]);

  return { orderBook, connected, error };
};

// --- MAIN EXPORTED COMPONENT ---
const BitmexOrderBook = ({ symbol = 'BTC', theme, height = '650px' }) => {
  // Default theme fallback if not provided
  const defaultTheme = {
    background: '#0a0a0a', textPrimary: '#e5e7eb', textSecondary: '#6b7280',
    cardBg: '#141414', border: '#262626'
  };
  const activeTheme = theme || defaultTheme;

  const { orderBook, connected, error } = useBitmexOrderBook(symbol);

  // Stats
  const maxBidVol = Math.max(...orderBook.bids.map(b => b.size), 1);
  const maxAskVol = Math.max(...orderBook.asks.map(a => a.size), 1);
  const spread = orderBook.asks[0] && orderBook.bids[0] 
    ? orderBook.asks[0].price - orderBook.bids[0].price 
    : 0;

  return (
    <div style={{ 
      background: activeTheme.cardBg, 
      border: `1px solid ${activeTheme.border}`, 
      borderRadius: '12px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: height,
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '0.75rem 1rem', 
        borderBottom: `1px solid ${activeTheme.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: activeTheme.textPrimary }}>
            Order Book
          </h3>
          <span style={{ fontSize: '0.75rem', color: '#ffa500', opacity: 0.8 }}>{symbol}-PERP</span>
        </div>
        <div style={{ 
          width: 8, height: 8, borderRadius: '50%', 
          background: connected ? '#00bfa5' : '#ff5253',
          boxShadow: connected ? '0 0 8px rgba(0, 191, 165, 0.5)' : 'none'
        }} />
      </div>

      {/* Disconnected State */}
      {!connected && orderBook.bids.length === 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: activeTheme.textSecondary, flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '2rem' }}>⚠️</div>
          <div style={{ fontSize: '0.85rem' }}>{error || 'Connecting...'}</div>
        </div>
      )}

      {/* Book Content */}
      {(connected || orderBook.bids.length > 0) && (
        <>
          {/* Column Headers */}
          <div style={{ 
            padding: '0.5rem 12px', fontSize: '0.7rem', display: 'flex', 
            justifyContent: 'space-between', color: activeTheme.textSecondary, 
            fontWeight: '600', textTransform: 'uppercase'
          }}>
            <span>Size</span><span>Price</span>
          </div>

          {/* ASKS (Sells) - Reversed Flex for bottom alignment */}
          <div style={{ flex: 1, overflowY: 'hidden', display: 'flex', flexDirection: 'column-reverse' }}>
            {orderBook.asks.slice(0, 15).map(ask => (
              <div key={ask.id} style={{ flexShrink: 0 }}>
                <OrderRow item={ask} maxVolume={maxAskVol} type="ask" theme={activeTheme} />
              </div>
            ))}
          </div>

          {/* SPREAD */}
          <div style={{ 
            padding: '0.5rem', textAlign: 'center', borderTop: `1px solid ${activeTheme.border}`,
            borderBottom: `1px solid ${activeTheme.border}`, background: 'rgba(255,255,255,0.03)'
          }}>
            <span style={{ color: activeTheme.textPrimary, fontWeight: 'bold', fontFamily: 'monospace' }}>
              {orderBook.bids[0] ? formatPrice(orderBook.bids[0].price) : '---'}
            </span>
            <span style={{ fontSize: '0.75rem', color: activeTheme.textSecondary, marginLeft: '8px' }}>
              Spread: <span style={{ color: '#ffa500' }}>{spread.toFixed(1)}</span>
            </span>
          </div>

          {/* BIDS (Buys) */}
          <div style={{ flex: 1, overflowY: 'hidden' }}>
            {orderBook.bids.slice(0, 15).map(bid => (
               <OrderRow key={bid.id} item={bid} maxVolume={maxBidVol} type="bid" theme={activeTheme} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BitmexOrderBook;